import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { ellipsis } from '@terra/shared-utils';
import { useUpdateFinding } from './useFindings';

const CONSTITUENT_NAMES_TOAST_MAX_CHARS = 200;

type PublishFindingParams = {
  findingId: string;
  isPublished: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getUnpublishedConstituentNames = (body: unknown): string => {
  if (!isRecord(body) || !Array.isArray(body.violations)) {
    return '';
  }

  return body.violations
    .flatMap((violation) => {
      if (
        !isRecord(violation) ||
        !Array.isArray(violation.unpublishedConstituents)
      ) {
        return [];
      }

      return violation.unpublishedConstituents;
    })
    .map((constituent) => {
      if (!isRecord(constituent)) {
        return '';
      }

      if (
        typeof constituent.title === 'string' &&
        constituent.title.length > 0
      ) {
        return constituent.title;
      }

      return typeof constituent.id === 'string' ? constituent.id : '';
    })
    .filter((name) => name.length > 0)
    .join(', ');
};

export const usePublishFinding = () => {
  const { updateFinding, isUpdating } = useUpdateFinding();
  const tFindingsRow = useTranslations('findingsTab.row');

  const publishFinding = useCallback(
    async ({ findingId, isPublished }: PublishFindingParams) => {
      try {
        const response = await updateFinding({
          findingId,
          patch: { isPublished },
        });

        if (response.status === 200) {
          toasterApi.success({
            title: tFindingsRow('publishUpdatedTitle'),
            description: isPublished
              ? tFindingsRow('publishedDescription')
              : tFindingsRow('unpublishedDescription'),
          });

          return true;
        }

        if (response.status === 409) {
          const constituentNames = getUnpublishedConstituentNames(
            response.body,
          );

          toasterApi.error({
            title: tFindingsRow('publishConstituentsErrorTitle'),
            description: tFindingsRow('publishConstituentsErrorDescription', {
              names: ellipsis(
                constituentNames,
                CONSTITUENT_NAMES_TOAST_MAX_CHARS,
              ),
            }),
          });

          return false;
        }

        throw new Error(tFindingsRow('publishUpdateErrorDescription'));
      } catch (error) {
        console.error('Failed to toggle finding publication:', error);
        toasterApi.error({
          title: tFindingsRow('publishUpdateErrorTitle'),
          description:
            error instanceof Error
              ? error.message
              : tFindingsRow('publishUpdateErrorDescription'),
        });

        return false;
      }
    },
    [updateFinding, tFindingsRow],
  );

  return {
    publishFinding,
    isPublishing: isUpdating,
  };
};
